<script setup>
import { computed } from 'vue';

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
    default: 'default',
    validator: value => ['small', 'default', 'large'].includes(value),
  },
  ariaLabel: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue']);

const sizeConfig = {
  small: {
    track: 'w-8 h-5',
    thumb: 'w-3 h-3',
    translate: 'translate-x-3',
  },
  default: {
    track: 'w-10 h-6',
    thumb: 'w-4 h-4',
    translate: 'translate-x-4',
  },
  large: {
    track: 'w-12 h-7',
    thumb: 'w-5 h-5',
    translate: 'translate-x-5',
  },
};

const currentSize = computed(() => sizeConfig[props.size]);

const wrapperClasses = computed(() => [
  'relative inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-n-brand',
  props.disabled && 'cursor-not-allowed opacity-50',
]);

const trackClasses = computed(() => [
  'relative rounded-full transition-colors',
  currentSize.value.track,
  props.modelValue ? 'bg-woot-500' : 'bg-slate-200 dark:bg-slate-700',
]);

const thumbClasses = computed(() => [
  'absolute left-1 top-1 bg-white rounded-full transition-transform',
  currentSize.value.thumb,
  props.modelValue && currentSize.value.translate,
]);

const handleToggle = () => {
  if (!props.disabled) {
    emit('update:modelValue', !props.modelValue);
  }
};
</script>

<template>
  <button
    type="button"
    role="switch"
    :aria-checked="modelValue"
    :aria-label="ariaLabel || undefined"
    :class="wrapperClasses"
    :disabled="disabled"
    @click="handleToggle"
  >
    <div :class="trackClasses">
      <div :class="thumbClasses" />
    </div>
  </button>
</template>
